import { request } from '@/shared/api/request'
import { camelize } from '@/shared/lib/camelize'

export interface ConversationParticipant {
  id: string
  username: string
  displayName: string
  avatarUrl?: string | null
}

/** The page a conversation came through (the other participant owns it). */
export interface ConversationPage {
  id: string
  username: string
  name?: string | null
  avatarUrl?: string | null
}

export interface Conversation {
  id: string
  lastMessageText?: string | null
  lastMessageAt?: string | null
  lastSenderId?: string | null
  unreadCount: number
  participants: ConversationParticipant[]
  /** Set when the conversation was started through a page (older servers: absent). */
  page?: ConversationPage | null
}

export interface ChatMessage {
  id: string
  text?: string | null
  /** Server's exact timestamp string - echoed back as the pagination cursor. */
  createdAt: string
  senderId: string
  senderUsername: string
  senderAvatar?: string | null
  isOwn: boolean
  /** JSON-encoded array of attachment urls once the pipeline published them. */
  mediaUrls?: string | null
  /** JSON-encoded array of posters, index-aligned with mediaUrls (videos carry theirs). */
  thumbnailUrls?: string | null
  /** MediaStatus: 10 created, 20 in progress (attachments still processing), 30 published. */
  mediaStatus?: number | null
  /** The page the message was sent through, when any (older servers: absent). */
  pageId?: string | null
  /** Client only: the picked files' previews while the attachments process. */
  localPreviews?: string[]
}

/** The attachments are uploaded but the pipeline has not published them yet. */
export function isMediaProcessing(message: ChatMessage): boolean {
  return message.mediaStatus === 20 && messageMediaList(message).length === 0
}

function decodeUrlArray(raw: string | null | undefined): string[] {
  if (!raw) return []
  try {
    const decoded: unknown = JSON.parse(raw)
    if (Array.isArray(decoded))
      return decoded.filter((url): url is string => typeof url === 'string')
    return typeof decoded === 'string' ? [decoded] : [raw]
  } catch {
    return [raw]
  }
}

export function messageMediaList(message: ChatMessage): string[] {
  return decodeUrlArray(message.mediaUrls)
}

/** Index-aligned with messageMediaList (a video attachment carries its poster). */
export function messageThumbnailList(message: ChatMessage): string[] {
  const media = messageMediaList(message)
  const thumbs = decodeUrlArray(message.thumbnailUrls)
  return thumbs.length === media.length ? thumbs : media
}

/** The iOS isVideoURL rule. */
export function isVideoUrl(url: string): boolean {
  const path = url.toLowerCase().split('?')[0] ?? ''
  return ['.mp4', '.mov', '.m4v', '.webm', '.m3u8'].some((extension) => path.endsWith(extension))
}

/** The person on the other side (participants include the current user). */
export function otherParticipant(
  conversation: Conversation,
  myUserId: string | null | undefined,
): ConversationParticipant | undefined {
  const me = myUserId?.toLowerCase()
  return (
    conversation.participants.find((participant) => participant.id.toLowerCase() !== me) ??
    conversation.participants[0]
  )
}

export function participantLabel(participant: ConversationParticipant | undefined): string {
  if (!participant) return ''
  return participant.displayName || participant.username
}

const BASE = '/api/messaging'

/** A cursor far in the future - with reverse=true this yields the latest messages. */
export const FAR_FUTURE_DATE = '2100-01-01T00:00:00Z'

export const messagingApi = {
  async getConversations(limit = 100, offset = 0): Promise<Conversation[]> {
    const data = await request<unknown>({
      url: `${BASE}/conversations`,
      params: { limit, offset },
    })
    return camelize<Conversation[]>(data)
  },

  /**
   * Returns the conversation id (existing or newly created) with `userId`.
   * `pageId` names the page the chat starts through (the page screen's
   * Message button); the server keeps it only when `userId` owns that page.
   */
  async startConversation(userId: string, pageId?: string | null): Promise<string> {
    const data = await request<Record<string, unknown>>({
      method: 'POST',
      url: `${BASE}/conversations`,
      data: pageId ? { user_id: userId, page_id: pageId } : { user_id: userId },
    })
    const id = data?.id ?? data?.conversation_id
    if (typeof id !== 'string') throw new Error('Missing conversation id in response')
    return id
  },

  async leaveConversation(id: string): Promise<void> {
    await request({ method: 'POST', url: `${BASE}/conversations/${id}/leave` })
  },

  /**
   * Backend pagination semantics: reverse=true returns messages *older* than
   * `date` (newest first), reverse=false returns messages *newer* than `date`
   * (oldest first).
   */
  async getMessages(
    conversationId: string,
    date: string,
    reverse: boolean,
    limit = 50,
  ): Promise<ChatMessage[]> {
    const data = await request<unknown>({
      url: `${BASE}/conversations/${conversationId}/messages`,
      params: { date, reverse: String(reverse), limit },
    })
    return camelize<ChatMessage[]>(data)
  },

  /**
   * Returns the new message's server id. `hasMedia` says attachments follow
   * through a messageAttachment upload session keyed by that id (the text
   * may be empty then). `pageId` tags a message sent from a chat opened
   * through a page.
   */
  async sendMessage(
    conversationId: string,
    text: string,
    hasMedia = false,
    pageId?: string | null,
  ): Promise<string> {
    const data = await request<Record<string, unknown>>({
      method: 'POST',
      url: `${BASE}/conversations/${conversationId}/messages`,
      data: pageId ? { text, has_media: hasMedia, page_id: pageId } : { text, has_media: hasMedia },
    })
    if (typeof data?.id !== 'string') throw new Error('Missing message id in response')
    return data.id
  },

  /**
   * Single message with its authoritative server timestamp - replaces the
   * optimistic echo so pagination cursors never rely on the device clock.
   */
  async getMessage(id: string): Promise<ChatMessage> {
    const data = await request<unknown>({ url: `${BASE}/messages/${id}` })
    return camelize<ChatMessage>(data)
  },

  async deleteMessage(id: string): Promise<void> {
    await request({ method: 'DELETE', url: `${BASE}/messages/${id}` })
  },

  /** Marks everything up to `date` (defaults to now) read; best-effort. */
  async markRead(conversationId: string, date?: string): Promise<void> {
    try {
      await request({
        method: 'PUT',
        url: `${BASE}/messages/read`,
        data: { conversation_id: conversationId, date: date ?? new Date().toISOString() },
      })
    } catch {
      /* best-effort */
    }
  },

  async getUnreadCount(): Promise<number> {
    try {
      const data = await request<Record<string, unknown>>({ url: `${BASE}/unread-count` })
      const value = data?.count ?? data?.unread_count ?? data?.unreadCount
      return typeof value === 'number' ? value : 0
    } catch {
      return 0
    }
  },
}
